import React, { useEffect, useRef, useState, useMemo } from "react";
import "./bookings.css";
import logo from "../../assets/images/logo.png";
import bars from "../../assets/images/bars.png";
import cross from "../../assets/images/cross.png";
import calendar from "../../assets/images/calendar.png";
import arrow from "../../assets/images/confirmArrow.png";
import { useNavigate } from "react-router-dom";
import CourtInputs from "./courtInputs";
import axios from "axios";
import Loader from "./loader";

function Bookings({ totalCourts, token }) {
  const navigate = useNavigate();

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const datePicker = useRef(null);

  const [loading, setLoading] = useState(true);
  const [navActive, setNavActive] = useState(false);
  const [selDate, setSelDate] = useState(new Date());
  const [numCourts, setNumCourts] = useState(1);
  const [uniformTiming, setUniformTiming] = useState(0);
  const [slots, setSlots] = useState([]);

  /* ──────────────────────────────────────────────────────────
       1. Fetch slot grid for the selected date
       ──────────────────────────────────────────────────────────*/
  const getDateBookings = async () => {
    setLoading(true);
    try {
      const formattedDate = `${selDate.getFullYear()}-${String(
        selDate.getMonth() + 1,
      ).padStart(2, "0")}-${String(selDate.getDate()).padStart(2, "0")}`;

      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/bookings/${formattedDate}/slots`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to fetch bookings");
      }

      setSlots(response.data); // [{slot:0,free:4}, …]
    } catch (error) {
      console.error("Error fetching bookings:", error);

      if (error.response?.status === 429) {
        alert("Too many requests. Please wait and try again later.");
      } else {
        alert(
          error?.response?.data?.error ||
            "An error occurred. Please try again later. If the problem persists, please call us to book directly, and let us know about the issue.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      getDateBookings();
    }
  }, [selDate, token]);

  /* ──────────────────────────────────────────────────────────
       2. Clamp numCourts to what’s actually free that day
       ──────────────────────────────────────────────────────────*/
  const maxFree = useMemo(
    () => (slots?.length ? Math.max(...slots.map((s) => s.free)) : 0),
    [slots],
  );

  useEffect(() => {
    if (numCourts > maxFree && maxFree > 0) {
      setNumCourts(maxFree); // auto-downgrade 4→2, etc.
    }
    if (maxFree === 0) {
      setNumCourts(1); // sold-out → default to 1
    }
  }, [maxFree, numCourts]);

  /* always force uniform mode when only 1 court */
  useEffect(() => {
    if (numCourts === 1) setUniformTiming(0);
  }, [numCourts]);

  /* ──────────────────────────────────────────────────────────
       UI helpers
       ──────────────────────────────────────────────────────────*/
  const allFull = useMemo(
    () => slots?.length > 0 && slots.every((s) => s.free === 0),
    [slots],
  );

  const handleUniformTiming = () =>
    numCourts === 1 ? null : (
      <select
        className="bookings-console-body-form-input"
        name="uniformTiming"
        onChange={(e) => setUniformTiming(Number(e.target.value))}
      >
        <option value="0">Uniform Timing</option>
        <option value="1">Custom Entry</option>
      </select>
    );

  const handleDateChange = (e) => {
    e.preventDefault(); // Add this
    e.stopPropagation(); // Add this

    const newDate = new Date(e.target.value + "T00:00:00");

    // Only update if the date actually changed
    if (newDate.getTime() !== selDate.getTime()) {
      setSelDate((prev) => newDate);
    }
  };

  const getDateToday = () => {
    // Get current date in Toronto timezone
    const torontoDate = new Date().toLocaleString("en-US", {
      timeZone: "America/Toronto",
    });
    const today = new Date(torontoDate);

    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(today.getDate()).padStart(2, "0")}`;
  };

  /* ──────────────────────────────────────────────────────────
       Confirm booking (stub)
       ──────────────────────────────────────────────────────────*/
  const handleConfirmBooking = async (e) => {
    e.preventDefault();
    setLoading(true);

    // disable submit button to prevent double-clicks
    e.target.querySelector("button").disabled = true;

    /* ---------- Check if date is in the past (Toronto time) ---------- */
    const torontoNow = new Date().toLocaleString("en-US", {
      timeZone: "America/Toronto",
    });
    const today = new Date(torontoNow);
    today.setHours(0, 0, 0, 0); // Reset to start of day

    const selectedDate = new Date(selDate);
    selectedDate.setHours(0, 0, 0, 0); // Reset to start of day

    if (selectedDate < today) {
      alert("Invalid date. Please select today or a future date.");
      setSelDate(new Date(torontoNow)); // Reset to current Toronto date
      e.target.querySelector("button").disabled = false;
      setLoading(false);
      return;
    }

    /* ---------- sanity check: slots have loaded ---------- */
    if (slots.length !== 48) {
      alert(
        "An error occurred fetching availability. Please refresh or call to book.",
      );
      e.target.querySelector("button").disabled = false;
      setLoading(false);
      return;
    }

    /* ---------- 1.  Extract & validate user info ---------- */
    const name = e.target.elements.name.value.trim();
    const email = e.target.elements.email.value.trim();
    const phone = e.target.elements.number.value.trim();
    const message = e.target.elements.message.value.trim();
    const terms = e.target.elements.terms.checked;

    if (!name || !email || !terms) {
      alert("Please fill in all fields and accept the terms and conditions.");
      e.target.querySelector("button").disabled = false;
      setLoading(false);
      return;
    }

    /* ---------- 2.  Common booking fields ---------- */
    const numCourts = Number(e.target.elements.numCourts.value);
    const uniformTiming = Number(e.target.elements.uniformTiming?.value || 0); // 0 = uniform
    const dateObj = selDate; // chosen date
    const date_num = dateObj.getDate();
    const month_num = dateObj.getMonth() + 1;
    const year_num = dateObj.getFullYear();

    const baseFields = {
      name,
      email,
      number: phone,
      date_num,
      month_num,
      year_num,
      type: "direct",
      status: "active",
      notes: message,
      terms,
    };

    /* ---------- 3.  Build bookings[] ---------- */
    const bookings = [];

    if (uniformTiming === 0) {
      /* ----- UNIFORM MODE ----- */
      const startTime = Number(e.target.elements.startTime.value);
      const duration = Number(e.target.elements.duration.value);
      const startSlot = Math.round(startTime * 2);
      const durSlots = Math.round(duration * 2);

      // Collect courts that are free for the *entire* block
      const possible = [];
      for (let cid = 1; cid <= totalCourts; cid++) {
        let ok = true;
        for (let s = 0; s < durSlots; s++) {
          const slot = slots[startSlot + s];
          if (!slot || !slot.freeCourts.includes(cid)) {
            ok = false;
            break;
          }
        }
        if (ok) possible.push(cid);
        if (possible.length === numCourts) break;
      }
      if (possible.length < numCourts) {
        alert(
          "Not enough courts available for the requested block. " +
            "Please pick a different time or reduce courts.",
        );
        e.target.querySelector("button").disabled = false;
        setLoading(false);
        return;
      }

      bookings.push({
        ...baseFields,
        time_num: startTime,
        duration,
        court_nums: possible, // exact court IDs
      });
    } else {
      /* ----- CUSTOM MODE ----- */
      const usedCourts = new Set();

      for (let idx = 0; idx < numCourts; idx++) {
        const startStr = e.target.elements[`startTime[${idx}]`]?.value;
        const durStr = e.target.elements[`duration[${idx}]`]?.value;
        if (!startStr || !durStr) {
          alert("Please fill all start times and durations.");
          e.target.querySelector("button").disabled = false;
          setLoading(false);
          return;
        }
        const startTime = Number(startStr);
        const duration = Number(durStr);
        const startSlot = Math.round(startTime * 2);
        const durSlots = Math.round(duration * 2);

        // Pick the first court not yet used that stays free for full run
        let chosen = null;
        for (let cid = 1; cid <= totalCourts; cid++) {
          if (usedCourts.has(cid)) continue;
          let ok = true;
          for (let s = 0; s < durSlots; s++) {
            const slot = slots[startSlot + s];
            if (!slot || !slot.freeCourts.includes(cid)) {
              ok = false;
              break;
            }
          }
          if (ok) {
            chosen = cid;
            break;
          }
        }
        if (chosen === null) {
          alert(
            `No single court is free for Court #${idx + 1}'s block. ` +
              "Please adjust times.",
          );
          e.target.querySelector("button").disabled = false;
          setLoading(false);
          return;
        }
        usedCourts.add(chosen);

        bookings.push({
          ...baseFields,
          time_num: startTime,
          duration,
          court_nums: [chosen],
        });
      }
    }

    /* ---------- 4.  For now: just log; later: POST to API ---------- */

    try {
      const payment_form = await axios.post(
        process.env.REACT_APP_API_URL + "/bookings/reserve",
        {
          bookings,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (![200, 202].includes(payment_form.status)) {
        e.target.querySelector("button").disabled = false;
        alert(
          "An error occurred while processing your booking. Please try again.",
        );
        return;
      }

      if (payment_form.status === 202) {
        navigate("/booking-verification", { state: { booking: { "change_token": payment_form.data, "email": email } } });
        return;
      }

      navigate("/booking-confirmation", { state: { booking: true } });
    } catch (error) {
      e.target.querySelector("button").disabled = false;
      alert(
        "An error occurred while processing your booking. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ──────────────────────────────────────────────────────────
       JSX
       ──────────────────────────────────────────────────────────*/
  return (
    <div className="bookings-page">
      {/* NAVBAR ================================================================= */}
      <div className="landing-nav">
        <div className="nav-logo">
          <img alt="Hymus Sports Logo" className="nav-logo-img" src={logo} />
        </div>
        <div className="nav-opts">
          <a
            className="nav-opt"
            onClick={() => {
              window.location.href = "/#home";
            }}
          >
            Home
          </a>
          <a
            className="nav-opt"
            onClick={() => {
              window.location.href = "/#facility";
            }}
          >
            Our Facility
          </a>
          <a
            className="nav-opt"
            onClick={() => {
              window.location.href = "/#rates";
            }}
          >
            Rates
          </a>
          <a
            className="nav-opt"
            onClick={() => {
              window.location.href = "/#about";
            }}
          >
            Contact
          </a>
        </div>
      </div>

      {/* MOBILE NAV ============================================================ */}
      <div
        className={
          navActive ? "mobile-landing-nav active" : "mobile-landing-nav"
        }
      >
        <div className="mobile-nav-header">
          <div className="mobile-nav-icon">
            <img
              alt="Navigation Icon"
              className="mobile-nav-icon-img"
              onClick={() => setNavActive(!navActive)}
              src={navActive ? cross : bars}
            />
          </div>
          <div className="mobile-nav-logo">
            <img alt="Navigation Logo" className="mobile-nav-logo-img" src={logo} />
          </div>
        </div>
        <div className="mobile-nav-body">
          {[
            ["/#home", "Home"],
            ["/#our-facility", "Our Facility"],
            ["/#rates", "Rates"],
            ["/#about", "Contact"],
          ].map(([href, label]) => (
            <div className="mobile-nav-opt" key={href}>
              <a
                className="mobile-nav-opt-link"
                href={href}
                onClick={() => setNavActive(false)}
              >
                {label}
              </a>
            </div>
          ))}
          <div className="mobile-nav-opt">
            <a
              className="mobile-nav-opt-link"
              onClick={() => setNavActive(false)}
              href="/online-booking"
            >
              Book Online <span className="mobile-nav-beta-tag">Beta</span>
            </a>
          </div>
        </div>
      </div>

      {/* CONSOLE ============================================================== */}
      <div className="bookings-container">
        <div className="bookings-console">
          <img alt="Hymus Sports Logo" className="bookings-console-img" src={logo} />
          <h1 className="bookings-console-header">
            Badminton Court Booking{" "}
            <span className="bookings-beta-tag">Beta</span>
          </h1>

          {!loading ? (
            <div className="bookings-console-body">
              {/* Header (date picker) */}
              <div className="bookings-console-body-header">
                <div className="bookings-console-body-header-ls">
                  <img
                    alt="Calendar Icon"
                    className="bookings-console-body-header-ls-img"
                    src={calendar}
                  />
                  <input
                    min={getDateToday()}
                    type="date"
                    ref={datePicker}
                    value={`${selDate.getFullYear()}-${String(selDate.getMonth() + 1).padStart(2, "0")}-${String(selDate.getDate()).padStart(2, "0")}`}
                    onChange={handleDateChange}
                    className="bookings-console-body-header-ls-input"
                  />
                  <label className="bookings-console-body-header-ls-label">
                    Calendar
                  </label>
                </div>
                <div className="bookings-console-body-header-ct">
                  <h2 className="bookings-console-body-header-ct-date">
                    {`${days[selDate.getDay()]}, ${months[selDate.getMonth()]} ${selDate.getDate()}, ${selDate.getFullYear()}`}
                  </h2>
                </div>
              </div>

              {/* FORM ======================================================= */}
              <form
                className="bookings-console-body-form"
                onSubmit={handleConfirmBooking}
              >
                {allFull && (
                  <p className="bookings-soldout">
                    Fully booked for this date.
                  </p>
                )}

                <input
                  className="bookings-console-body-form-input"
                  name="name"
                  type="text"
                  pattern="[A-Za-z\s\-'\.]{1,50}"
                  placeholder="Name*"
                  required
                  disabled={allFull}
                />

                <input
                  className="bookings-console-body-form-input"
                  name="number"
                  type="text"
                  pattern="[\d\-]{6,15}"
                  placeholder="Phone Number*"
                  required
                  disabled={allFull}
                />

                <input
                  className="bookings-console-body-form-input"
                  name="email"
                  type="email"
                  placeholder="Email Address*"
                  required
                  disabled={allFull}
                />

                <select
                  className="bookings-console-body-form-input"
                  name="numCourts"
                  value={numCourts}
                  onChange={(e) => setNumCourts(Number(e.target.value))}
                  required
                  disabled={allFull}
                >
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n} disabled={n > maxFree}>
                      {n} Court{n > 1 ? "s" : ""}
                    </option>
                  ))}
                  <option disabled>5+ (Please Call Directly to Book)</option>
                </select>

                {handleUniformTiming()}

                <CourtInputs
                  numCourts={numCourts}
                  bookingDate={selDate}
                  uniformTiming={uniformTiming}
                  slots={slots}
                  totalCourts={totalCourts}
                />

                <textarea
                  rows={1}
                  className="bookings-console-body-form-input"
                  name="message"
                  pattern="[A-Za-z0-9\s\-'\.,:;!?\(\)]{0,250}"
                  title="Notes can contain letters, numbers, spaces, and basic punctuation (max 250 characters)"
                  placeholder="Leave us a note here (e.g. specific court numbers)"
                  maxLength="250"
                />

                <div className="bookings-console-body-form-checkbox-cont">
                  <input
                    className="bookings-console-body-form-checkbox"
                    type="checkbox"
                    name="terms"
                    required
                  />

                  <label className="bookings-console-body-form-checkbox-label">
                    I agree to the{" "}
                    <a
                      className="bookings-console-body-form-terms"
                      target="_blank"
                      href="/terms-and-conditions"
                    >
                      Terms & Conditions
                    </a>
                  </label>
                </div>

                <button
                  className="bookings-console-body-form-btn"
                  type="submit"
                  disabled={allFull}
                >
                  <span className="bookings-console-body-form-btn-txt">
                    Confirm
                  </span>
                  <img
                    className="bookings-console-body-form-btn-img"
                    src={arrow}
                    alt="Hymus Sports"
                  />
                </button>
              </form>
            </div>
          ) : (
            <div className="bookings-console-body">
              <Loader />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Bookings;
